// PRD 13 §3.2–§3.4 — full-page create/edit notice form (prototype parity: a
// full-page swap, not a slide panel). Drives the audience/category selects,
// the patient-app push checkbox (auto-disabled for STAFF audience), title, and
// the contenteditable RTE. Publish shows a confirm modal; Draft saves silently;
// Delete (edit + Root) confirms then returns to the list.
//
// SHCT-838 — the publish intent comes from the BUTTON, not from a field: `[임시저장]` sends
// DRAFT and `[게시]` sends PUBLISHED (PRD 13 v1.6 §4.2). There is no separate publish
// endpoint (contract §3.3/§3.4). Author is the logged-in admin: in mock
// mode we push it into the mock via setNoticeMockAuthor; the real BE derives it
// from the session.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';

import { useAuthStore, type PortalRole } from '@/app/store/auth.store';
import { ConfirmModal } from '@/components/feedback/ConfirmModal';
import { InfoModal } from '@/components/feedback/InfoModal';
import { toast } from '@/components/feedback/useToast';
import { normalizeRichHtml } from '@/shared/utils/normalize-rich-html';

import { setNoticeMockAuthor } from '../api/notices.mock';
import { useCreateNotice, useUpdateNotice } from '../hooks/use-notice-mutations';
import { useNoticeDetailQuery } from '../hooks/use-notices';
import { useNoticesPageStore } from '../store/notices-page.store';
import {
  NOTICE_AUDIENCES,
  NOTICE_CATEGORIES,
  NOTICE_FORM_STATUSES,
  NoticeApiError,
  type CreateNoticeInput,
  type Notice,
  type NoticeAudience,
  type NoticeAuthor,
  type NoticeCategory,
} from '../types/notices.types';
import { getNoticeErrorMessage } from '../utils/error-message';
import { formatNoticeDateTime } from '../utils/format';
import {
  hasContent,
  hasEmoji,
  hasEmojiInRichText,
  isContentTooLong,
  isTitleLengthValid,
} from '../utils/validation';
import { DeleteNoticeConfirm } from './DeleteNoticeConfirm';
import { NoticeRteToolbar, type RteCommand } from './NoticeRteToolbar';
import { useWriteGuard } from '@/shared/hooks/useWriteGuard';

type FormStatus = (typeof NOTICE_FORM_STATUSES)[number];

interface InitialValues {
  audience: NoticeAudience;
  category: NoticeCategory;
  pushOnPublish: boolean;
  title: string;
  content: string;
  author: NoticeAuthor;
  datetimeLabel: string;
}

interface NoticeFormProps {
  mode: 'create' | 'edit';
  noticeId: number | null;
  initial: InitialValues;
  // The loaded notice in edit mode — supplies id + title to the delete confirm.
  editingNotice?: Notice;
}

function NoticeForm({ mode, noticeId, initial, editingNotice }: NoticeFormProps) {
  const { t } = useTranslation('notices');
  const closeForm = useNoticesPageStore((s) => s.closeForm);

  // Defense-in-depth: the form is only reachable via WRITE-gated controls, but
  // disable submission if WRITE is lost mid-session (e.g. dev role switch).
  const { canWrite, denyProps, tooltip } = useWriteGuard('PUBLICATION');
  const isRoot = useAuthStore((s) => s.isRoot);
  const isEdit = mode === 'edit';

  const createMutation = useCreateNotice();
  const updateMutation = useUpdateNotice();
  const submitting = createMutation.isPending || updateMutation.isPending;
  // SHCT-582 — PRD 21 §7.4 P-2. QC's re-test found this form fully editable under
  // PUBLICATION:READ — only the two footer buttons were gated, so a notice could be written out in
  // full before the refusal surfaced. Every author-facing control now carries the same gate.
  const fieldsDisabled = !canWrite || submitting;

  const [audience, setAudience] = useState<NoticeAudience>(initial.audience);
  const [category, setCategory] = useState<NoticeCategory>(initial.category);
  const [pushOnPublish, setPushOnPublish] = useState(initial.pushOnPublish);
  const [title, setTitle] = useState(initial.title);
  // Mirror of the contenteditable HTML — the editor stays uncontrolled (innerHTML
  // set once on mount), but we track its value in state so button-enable and the
  // unsaved-changes guard can react to edits without re-controlling the DOM.
  const [contentHtml, setContentHtml] = useState(initial.content);
  const [errors, setErrors] = useState<{ title?: string; content?: string }>({});
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDiscard, setShowDiscard] = useState(false);
  // PRD §8.2 + clarification (18/5): a transaction API failure (save/publish) is
  // shown in a popup dialog, not a toast.
  const [errorModalMessage, setErrorModalMessage] = useState<string | null>(null);

  const titleRef = useRef<HTMLInputElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);

  // PRD 13 §4.3 — STAFF audience can never push to the patient app.
  const pushDisabled = audience === 'STAFF';

  // Hydrate the contenteditable once on mount (keyed remount per notice id —
  // see NoticeFormPanel). React never re-controls innerHTML, so keystrokes
  // mutate the DOM directly; we read it back only on submit.
  useEffect(() => {
    if (editorRef.current) editorRef.current.innerHTML = initial.content;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAudienceChange = (next: NoticeAudience) => {
    setAudience(next);
    if (next === 'STAFF') setPushOnPublish(false);
  };

  const handleCommand = (cmd: RteCommand) => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    // document.execCommand is deprecated but still supported in every shipping
    // browser; PRD 13 §2.2 #12 mandates parity with the existing editors.
    document.execCommand(cmd, false);
    // Toolbar edits don't always emit `input` — sync the mirror explicitly.
    setContentHtml(normalizeRichHtml(editorRef.current.innerHTML));
  };

  // SHCT-678 — `execCommand` emits the legacy presentational tags `<b>`/`<i>`,
  // which the BE sanitizer's element allow-list drops on save (it keeps the
  // semantic `<strong>`/`<em>`/`<u>`), so Bold/Italic vanished after a
  // save→reload round-trip while Underline survived. Normalizing the value that
  // leaves the editor folds them down to the canonical vocabulary. Same shared
  // helper the 건강백과 / 상담매뉴얼 / 관리지침 editors already use.
  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const html = e.clipboardData.getData('text/html');
    if (!html) return; // plain-text paste is already canonical
    e.preventDefault();
    document.execCommand('insertHTML', false, normalizeRichHtml(html));
    setContentHtml(normalizeRichHtml(editorRef.current?.innerHTML ?? ''));
  };

  const validate = (): { title?: string; content?: string } => {
    const errs: { title?: string; content?: string } = {};
    const trimmed = title.trim();
    // [공통] 00 §4.2 — TITLE: 2~100 chars, emoji not allowed.
    if (!isTitleLengthValid(trimmed)) {
      errs.title = t('form.validation.titleLength');
    } else if (hasEmoji(trimmed)) {
      errs.title = t('form.validation.titleEmoji');
    }
    // Measure the NORMALIZED html — that is what actually gets stored, so the
    // 20,000-char cap has to be applied to it rather than to the raw editor DOM.
    const html = normalizeRichHtml(editorRef.current?.innerHTML ?? '');
    // [공통] 00 §7-k — content required, max 20,000 chars (HTML included).
    if (!hasContent(html)) {
      errs.content = t('form.validation.contentRequired');
    } else if (isContentTooLong(html)) {
      errs.content = t('form.validation.contentTooLong');
    } else if (hasEmojiInRichText(html)) {
      // SHCT-858 — §4.2 defines no RICHTEXT class: 리치텍스트 is carved out by §3
      // for its 20,000자 LENGTH cap only, so the emoji ban still applies here.
      errs.content = t('common:errors.emojiNotAllowed');
    }
    return errs;
  };

  const focusFirstError = (errs: { title?: string; content?: string }) => {
    if (errs.title) titleRef.current?.focus();
    else if (errs.content) editorRef.current?.focus();
  };

  // [공통] 00 §1.2 — validate on field blur (per-field message), independent of
  // the submit-time full validation.
  const handleTitleBlur = () => {
    const trimmed = title.trim();
    const msg = !isTitleLengthValid(trimmed)
      ? t('form.validation.titleLength')
      : hasEmoji(trimmed)
        ? t('form.validation.titleEmoji')
        : undefined;
    setErrors((e) => ({ ...e, title: msg }));
  };

  const handleContentBlur = () => {
    const html = normalizeRichHtml(editorRef.current?.innerHTML ?? '');
    const msg = !hasContent(html)
      ? t('form.validation.contentRequired')
      : isContentTooLong(html)
        ? t('form.validation.contentTooLong')
        : hasEmojiInRichText(html)
          ? t('common:errors.emojiNotAllowed')
          : undefined;
    setErrors((e) => ({ ...e, content: msg }));
  };

  // [공통] 00 §1.2 — action buttons stay disabled until the required fields pass.
  // Title + content are the required free-text fields (audience/category/status
  // always have a selected value).
  const titleValid = (() => {
    const trimmed = title.trim();
    return isTitleLengthValid(trimmed) && !hasEmoji(trimmed);
  })();
  const contentValid =
    hasContent(contentHtml) && !isContentTooLong(contentHtml) && !hasEmojiInRichText(contentHtml);
  const formValid = titleValid && contentValid;

  // Unsaved-changes guard for the [← back to list] button: any field diverging
  // from the loaded values marks the form dirty.
  const isDirty =
    audience !== initial.audience ||
    category !== initial.category ||
    pushOnPublish !== initial.pushOnPublish ||
    title !== initial.title ||
    contentHtml !== initial.content;

  const handleBack = () => {
    if (submitting) return;
    if (isDirty) setShowDiscard(true);
    else closeForm();
  };

  const buildInput = (targetStatus: FormStatus): CreateNoticeInput => ({
    audience,
    title: title.trim(),
    category,
    status: targetStatus,
    // SHCT-678 — fold `<b>`/`<i>` (and pasted inline styles) into the canonical
    // tags the BE sanitizer allow-lists, so the formatting survives the save.
    content: normalizeRichHtml(editorRef.current?.innerHTML ?? ''),
    pushOnPublish: audience === 'STAFF' ? false : pushOnPublish,
  });

  const submit = async (targetStatus: FormStatus) => {
    const errs = validate();
    if (errs.title || errs.content) {
      setErrors(errs);
      focusFirstError(errs);
      return;
    }
    setErrors({});
    const input = buildInput(targetStatus);
    try {
      if (isEdit && noticeId !== null) {
        await updateMutation.mutateAsync({ id: noticeId, input });
      } else {
        await createMutation.mutateAsync(input);
      }
      toast.success(targetStatus === 'DRAFT' ? t('toasts.draftSaved') : t('toasts.published'));
      closeForm();
    } catch (err) {
      // Map BE field errors to the inline error map; code-only errors → toast.
      if (err instanceof NoticeApiError && err.fields && Object.keys(err.fields).length > 0) {
        setErrors({ title: err.fields.title, content: err.fields.content });
      } else {
        // System/transaction error → popup dialog (PRD §8.2), not a toast.
        setErrorModalMessage(getNoticeErrorMessage(err, t, 'toasts.saveError'));
      }
    }
  };

  const handlePublishClick = () => {
    const errs = validate();
    if (errs.title || errs.content) {
      setErrors(errs);
      focusFirstError(errs);
      return;
    }
    setErrors({});
    setShowPublishConfirm(true);
  };

  const headerTitle = isEdit ? t('form.editTitle') : t('form.newTitle');
  const writeTooltip = canWrite ? undefined : t('tooltips.noWritePermission');

  return (
    <>
      <div className="content__header">
        <div>
          <h1 className="content__title">{headerTitle}</h1>
        </div>
        <div className="content__actions">
          <button
            type="button"
            className="btn btn-default btn-secondary"
            onClick={handleBack}
            disabled={submitting}
          >
            <ArrowLeft size={16} />
            {t('form.back')}
          </button>
        </div>
      </div>

      <div
        className="content__body"
        style={{ maxWidth: 960, margin: '0 auto', width: '100%' }}
      >
        <div
          className="card"
          style={{
            padding: 'var(--space-6)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-6)',
          }}
        >
          {/* Author / datetime — auto-filled, read-only (PRD §3.2). */}
          <div
            style={{
              display: 'flex',
              gap: 'var(--space-6)',
              background: 'var(--muted)',
              padding: 'var(--space-4)',
              borderRadius: 'var(--radius-md)',
            }}
          >
            <div style={{ flex: 1 }}>
              <div style={{ color: 'var(--muted-foreground)', fontSize: 12, marginBottom: 4 }}>
                {t('form.author.writer')}
              </div>
              <div style={{ fontWeight: 600 }}>
                {initial.author.name}{' '}
                <span className="badge badge-outline" style={{ fontSize: 11, marginLeft: 4 }}>
                  {initial.author.authorGrade}
                </span>{' '}
                <span style={{ fontWeight: 400, color: 'var(--muted-foreground)', marginLeft: 8 }}>
                  {initial.author.email}
                </span>
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ color: 'var(--muted-foreground)', fontSize: 12, marginBottom: 4 }}>
                {t('form.author.datetime')}
              </div>
              <div style={{ fontWeight: 500 }}>{initial.datetimeLabel}</div>
            </div>
          </div>

          {/* Audience radios (PRD §2.2 #6) */}
          <div className="form-field">
            <label className="form-field__label">
              {t('form.fields.audience.label')} <span className="required">*</span>
            </label>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', padding: '8px 0', flexWrap: 'wrap' }}>
              {NOTICE_AUDIENCES.map((code) => (
                <label key={code} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="noticeAudience"
                    value={code}
                    checked={audience === code}
                    onChange={() => handleAudienceChange(code)}
                    disabled={fieldsDisabled}
                    {...denyProps}
                    style={{ accentColor: 'var(--info)', cursor: 'pointer' }}
                  />
                  {t(`audiences.${code}`)}
                </label>
              ))}
              <span style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>
                {t('form.fields.audience.hint')}
              </span>
            </div>
          </div>

          {/* Category + Status */}
          <div style={{ display: 'flex', gap: 'var(--space-4)' }}>
            <div className="form-field" style={{ flex: 1 }}>
              <label className="form-field__label">
                {t('form.fields.category.label')} <span className="required">*</span>
              </label>
              <select
                className="form-field__select"
                value={category}
                onChange={(e) => setCategory(e.target.value as NoticeCategory)}
                disabled={fieldsDisabled}
                {...denyProps}
              >
                {NOTICE_CATEGORIES.map((code) => (
                  <option key={code} value={code}>
                    {t(`categories.${code}`)}
                  </option>
                ))}
              </select>
            </div>
            {/* SHCT-838 — the 「상태」 select used to sit here. PRD 13 v1.6 §4.2 now fixes the
                status as `[임시저장]` → DRAFT / `[게시]` → PUBLISHED and deletes §4 표 9번: the
                screen had two inputs for one value and no stated precedence, so picking 임시저장
                here and then clicking [게시] published anyway — and a notice publishes to every
                user with a push, which is not something you take back. `HIDDEN` stays an internal
                state and was never offered here. */}
          </div>

          {/* Push checkbox (PRD §2.2 #10) */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              padding: 'var(--space-3) var(--space-4)',
              background: 'var(--muted)',
              borderRadius: 'var(--radius-md)',
            }}
          >
            <label
              style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: pushDisabled ? 'not-allowed' : 'pointer' }}
              title={pushDisabled ? t('form.fields.push.disabledTooltip') : undefined}
            >
              <input
                type="checkbox"
                checked={pushOnPublish}
                disabled={pushDisabled || fieldsDisabled}
                {...denyProps}
                onChange={(e) => setPushOnPublish(e.target.checked)}
                style={{ width: 18, height: 18, accentColor: 'var(--info)', cursor: pushDisabled ? 'not-allowed' : 'pointer' }}
              />
              {t('form.fields.push.label')}
            </label>
            <span style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>
              {pushDisabled ? t('form.fields.push.disabledTooltip') : t('form.fields.push.hint')}
            </span>
          </div>

          {/* Title */}
          <div className="form-field">
            <label className="form-field__label">
              {t('form.fields.title.label')} <span className="required">*</span>
            </label>
            <input
              ref={titleRef}
              type="text"
              className={`form-field__input${errors.title ? ' form-field__input--error' : ''}`}
              placeholder={t('form.fields.title.placeholder')}
              value={title}
              maxLength={100}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={handleTitleBlur}
              disabled={fieldsDisabled}
              {...denyProps}
            />
            {errors.title ? <p className="form-field__error">{errors.title}</p> : null}
          </div>

          {/* Content RTE */}
          <div className="form-field">
            <label className="form-field__label">
              {t('form.fields.content.label')} <span className="required">*</span>
            </label>
            {canWrite ? <NoticeRteToolbar onCommand={handleCommand} /> : null}
            <div
              ref={editorRef}
              contentEditable={canWrite}
              suppressContentEditableWarning
              // SHCT-673 — `rte-content` restores the list padding/markers the
              // global `* { padding: 0 }` reset strips, so the • 목록 toolbar
              // button produces a visible bulleted list.
              className="rte-content"
              onInput={
                canWrite
                  ? () => setContentHtml(normalizeRichHtml(editorRef.current?.innerHTML ?? ''))
                  : undefined
              }
              onPaste={canWrite ? handlePaste : undefined}
              onBlur={canWrite ? handleContentBlur : undefined}
              style={{
                minHeight: 280,
                border: '1px solid var(--border)',
                // The top corners are square only because the toolbar sits flush above; without
                // it (READ-only) the box is on its own and rounds all four.
                borderRadius: canWrite
                  ? '0 0 var(--radius-sm) var(--radius-sm)'
                  : 'var(--radius-sm)',
                padding: '10px 12px',
                background: '#fff',
                fontSize: 14,
                lineHeight: 1.6,
                outline: 'none',
                whiteSpace: 'pre-wrap',
              }}
            />
            {errors.content ? <p className="form-field__error">{errors.content}</p> : null}
          </div>

          {/* Actions */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 'var(--space-2)',
              paddingTop: 'var(--space-6)',
              borderTop: '1px solid var(--border)',
            }}
          >
            {/* PRD §3.4 / §8.3 — Delete is edit-only, visible to WRITE+ but
                enabled only for Root; non-Root sees it disabled with a tooltip. */}
            {isEdit ? (
              // The tooltip lives on a wrapper <span>: `.btn:disabled` sets
              // `pointer-events: none`, so a `title` on the disabled button
              // itself would never surface on hover (SHCT-602).
              <span
                title={isRoot ? undefined : t('tooltips.noDeletePermission')}
                style={{
                  marginRight: 'auto',
                  display: 'inline-block',
                  cursor: isRoot ? 'pointer' : 'not-allowed',
                }}
              >
                <button
                  type="button"
                  className="btn btn-default btn-destructive"
                  onClick={() => {
                    if (!isRoot) return;
                    setShowDeleteConfirm(true);
                  }}
                  disabled={submitting || !isRoot}
                >
                  {t('form.actions.delete')}
                </button>
              </span>
            ) : null}
            <button
              type="button"
              className="btn btn-default btn-secondary"
              onClick={() => void submit('DRAFT')}
              disabled={submitting || !canWrite || !formValid}
              {...denyProps}
              title={writeTooltip}
            >
              {submitting ? t('form.actions.saving') : t('form.actions.draft')}
            </button>
            <button
              type="button"
              className="btn btn-default btn-primary"
              onClick={handlePublishClick}
              disabled={submitting || !canWrite || !formValid}
              {...denyProps}
              title={writeTooltip}
            >
              {t('form.actions.publish')}
            </button>
          </div>
        </div>
      </div>

      {/* PRD 13 §3.2 — the confirmation copy branches on 대상. `audience` is read
          from live form state here, not cached at open time: it is a radio and can
          change while the panel is open. */}
      <ConfirmModal
        open={showPublishConfirm}
        title={t('form.confirmPublish.title')}
        message={t(`form.confirmPublish.message.${audience}`)}
        confirmLabel={t('form.confirmPublish.confirm')}
        cancelLabel={t('form.confirmPublish.cancel')}
        confirmVariant="primary"
        loading={submitting}
        loadingLabel={t('form.actions.saving')}
        onConfirm={() => {
          setShowPublishConfirm(false);
          void submit('PUBLISHED');
        }}
        onCancel={() => {
          if (submitting) return;
          setShowPublishConfirm(false);
        }}
      />

      {/* Unsaved-changes guard for [← back to list] (shared wording, SHCT-448). */}
      <ConfirmModal
        open={showDiscard}
        title={t('form.discard.title')}
        message={t('form.discard.message')}
        confirmLabel={t('form.discard.confirm')}
        cancelLabel={t('form.discard.cancel')}
        confirmVariant="destructive"
        onConfirm={() => {
          setShowDiscard(false);
          closeForm();
        }}
        onCancel={() => setShowDiscard(false)}
      />

      <DeleteNoticeConfirm
        notice={showDeleteConfirm && editingNotice ? editingNotice : null}
        onClose={() => setShowDeleteConfirm(false)}
        onDeleted={closeForm}
      />

      <InfoModal
        open={errorModalMessage !== null}
        title={t('form.saveErrorTitle')}
        body={errorModalMessage ?? ''}
        onClose={() => setErrorModalMessage(null)}
      />
    </>
  );
}

function FormSkeleton() {
  return (
    <div className="content__body">
      <div className="card" style={{ padding: 'var(--space-6)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[0, 1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="animate-pulse"
              style={{ height: 40, background: 'var(--muted)', borderRadius: 'var(--radius-sm)' }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function deriveAuthorGrade(isRoot: boolean, roles: string[]): PortalRole {
  if (isRoot) return 'ROOT';
  return (roles[0] as PortalRole) ?? 'CONTENT';
}

export function NoticeFormPanel() {
  const { t } = useTranslation('notices');
  const view = useNoticesPageStore((s) => s.view);
  const closeForm = useNoticesPageStore((s) => s.closeForm);

  const name = useAuthStore((s) => s.name);
  const email = useAuthStore((s) => s.email);
  const roles = useAuthStore((s) => s.roles);
  const isRoot = useAuthStore((s) => s.isRoot);

  const isEdit = view?.mode === 'edit';
  const editId = view?.mode === 'edit' ? view.id : null;

  const currentAuthor = useMemo<NoticeAuthor>(
    () => ({ name, email, authorGrade: deriveAuthorGrade(isRoot, roles) }),
    [name, email, isRoot, roles],
  );

  // PRD §3.2 — mock author = logged-in admin (no-op for the real BE, which
  // derives author from the session). Set before any create call.
  useEffect(() => {
    setNoticeMockAuthor(currentAuthor);
  }, [currentAuthor]);

  // "Now" stamped once on mount for the create header (display only — the BE
  // assigns the real createdAt).
  const nowLabel = useMemo(() => formatNoticeDateTime(new Date().toISOString()), []);

  const { data, isLoading, error } = useNoticeDetailQuery(isEdit ? editId : null);

  // PRD §8.3 — opening a deleted/absent notice: notify then redirect to the list.
  const isNotFound =
    isEdit && error instanceof NoticeApiError && error.code === 'NOT_FOUND';
  useEffect(() => {
    if (isNotFound) {
      toast.error(t('errors.NOT_FOUND'));
      closeForm();
    }
  }, [isNotFound, closeForm, t]);

  if (!view) return null;

  if (isEdit) {
    if (isLoading) {
      return (
        <>
          <FormHeaderOnly titleKey="form.editTitle" />
          <FormSkeleton />
        </>
      );
    }
    if (error || !data) {
      // Not-found is handled by the redirect effect above — show the skeleton
      // (not the generic load error) for the frame before navigation.
      if (isNotFound) {
        return (
          <>
            <FormHeaderOnly titleKey="form.editTitle" />
            <FormSkeleton />
          </>
        );
      }
      return <FormLoadError onBack={closeForm} message={t('form.loadError')} />;
    }
    const notice: Notice = data;
    const initial: InitialValues = {
      audience: notice.audience,
      category: notice.category,
      pushOnPublish: notice.audience === 'STAFF' ? false : notice.pushOnPublish,
      title: notice.title,
      content: notice.content,
      author: notice.author,
      datetimeLabel: formatNoticeDateTime(notice.createdAt),
    };
    return (
      <NoticeForm
        key={`edit-${notice.id}`}
        mode="edit"
        noticeId={notice.id}
        initial={initial}
        editingNotice={notice}
      />
    );
  }

  const initial: InitialValues = {
    audience: 'PATIENT',
    category: 'GENERAL',
    pushOnPublish: false,
    title: '',
    content: '',
    author: currentAuthor,
    datetimeLabel: nowLabel,
  };
  return <NoticeForm key="create" mode="create" noticeId={null} initial={initial} />;
}

function FormHeaderOnly({ titleKey }: { titleKey: string }) {
  const { t } = useTranslation('notices');
  const closeForm = useNoticesPageStore((s) => s.closeForm);
  return (
    <div className="content__header">
      <div>
        <h1 className="content__title">{t(titleKey)}</h1>
      </div>
      <div className="content__actions">
        <button type="button" className="btn btn-default btn-secondary" onClick={closeForm}>
          <ArrowLeft size={16} />
          {t('form.back')}
        </button>
      </div>
    </div>
  );
}

function FormLoadError({ message, onBack }: { message: string; onBack: () => void }) {
  const { t } = useTranslation('notices');
  return (
    <>
      <FormHeaderOnly titleKey="form.editTitle" />
      <div className="content__body">
        <div className="card" style={{ padding: 'var(--space-6)' }}>
          <p role="alert" style={{ color: 'var(--destructive)', margin: 0 }}>
            {message}
          </p>
          <button type="button" className="btn btn-sm btn-outline" style={{ marginTop: 16 }} onClick={onBack}>
            {t('form.back')}
          </button>
        </div>
      </div>
    </>
  );
}
